import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, Package, MapPin, FileText, Users, Building, Calendar, Upload, Link2, Globe, Info, Inbox, PhoneCall, UserCheck } from 'lucide-react';
import PackagesManager from './PackagesManager';
import DestinationsManager from './DestinationsManager';
import CuratedDestinationsManager from './CuratedDestinationsManager';
import BlogManager from './BlogManager';
import PartnersManager from './PartnersManager';
import PartnerPackagesManager from './PartnerPackagesManager';
import MicrositesManager from './MicrositesManager';
import ConsultationsManager from './ConsultationsManager';
import ContentUpload from './ContentUpload';
import SiteSettingsCard from './SiteSettingsCard';
import AboutManager from './AboutManager';
import LeadsManager from './LeadsManager';
import CustomerProfileView from './CustomerProfileView';
import ContactManager from './ContactManager';
import MembersManager from './MembersManager';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    packages: 0,
    destinations: 0,
    blogPosts: 0,
    consultations: 0
  });

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Overview', icon: Building },
    { path: '/admin/about', label: 'About Page', icon: Info },
    { path: '/admin/contact', label: 'Contact Page', icon: PhoneCall },
    { path: '/admin/packages', label: 'Packages', icon: Package },
    { path: '/admin/destinations', label: 'Hot Destinations', icon: MapPin },
    { path: '/admin/curated-destinations', label: 'Curated Destinations', icon: Globe },
    { path: '/admin/blog', label: 'Blog Posts', icon: FileText },
    { path: '/admin/partners', label: 'Partners', icon: Users },
    { path: '/admin/partner-packages', label: 'Partner Packages', icon: Link2 },
    { path: '/admin/leads', label: 'Quote Leads', icon: Inbox },
    { path: '/admin/members', label: 'Members', icon: UserCheck },
    { path: '/admin/microsites', label: 'Client Microsites', icon: Building },
    { path: '/admin/consultations', label: 'Consultations', icon: Calendar },
    { path: '/admin/upload', label: 'Upload Content', icon: Upload },
  ];

  return (
    <div className="min-h-screen bg-cream paper-texture" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-maroon text-cream border-b border-maroon/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/logos/tamarin-light.png" 
              alt="Chimba Travel" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-xl font-heading font-bold">Chimba Travel Admin</h1>
              <p className="text-xs text-cream/70">Welcome back, {user?.name}</p>
            </div>
          </div>
          <button 
            onClick={handleLogout} 
            className="flex items-center space-x-2 text-cream hover:text-cream/80 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen bg-white border-r-2 border-maroon/20">
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-maroon text-cream'
                      : 'text-maroon hover:bg-maroon/10'
                  }`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Routes>
            <Route path="dashboard" element={
              <div data-testid="admin-overview">
                <h2 className="text-3xl font-heading font-bold text-maroon mb-8">Dashboard Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="card">
                    <Package className="text-terracotta mb-3" size={32} />
                    <p className="text-3xl font-heading font-bold text-maroon">{stats.packages}</p>
                    <p className="text-maroon/70">Travel Packages</p>
                  </div>
                  <div className="card">
                    <MapPin className="text-terracotta mb-3" size={32} />
                    <p className="text-3xl font-heading font-bold text-maroon">{stats.destinations}</p>
                    <p className="text-maroon/70">Hot Destinations</p>
                  </div>
                  <div className="card">
                    <FileText className="text-terracotta mb-3" size={32} />
                    <p className="text-3xl font-heading font-bold text-maroon">{stats.blogPosts}</p>
                    <p className="text-maroon/70">Blog Posts</p>
                  </div>
                  <div className="card">
                    <Calendar className="text-terracotta mb-3" size={32} />
                    <p className="text-3xl font-heading font-bold text-maroon">{stats.consultations}</p>
                    <p className="text-maroon/70">Consultations</p>
                  </div>
                </div>
                <SiteSettingsCard />
              </div>
            } />
            <Route path="packages" element={<PackagesManager />} />
            <Route path="about" element={<AboutManager />} />
            <Route path="contact" element={<ContactManager />} />
            <Route path="destinations" element={<DestinationsManager />} />
            <Route path="curated-destinations" element={<CuratedDestinationsManager />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="partners" element={<PartnersManager />} />
            <Route path="partner-packages" element={<PartnerPackagesManager />} />
            <Route path="leads" element={<LeadsManager />} />
            <Route path="members" element={<MembersManager />} />
            <Route path="customers/:userId" element={<CustomerProfileView />} />
            <Route path="microsites" element={<MicrositesManager />} />
            <Route path="consultations" element={<ConsultationsManager />} />
            <Route path="upload" element={<ContentUpload />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;